/**
 * Admin shell.
 *
 * Routing is gated on session state rather than on a redirect: while auth is
 * resolving nothing renders, and an unauthenticated user only ever sees Login.
 * That makes it structurally impossible to flash admin content before the
 * session check completes.
 */
import { Routes, Route, NavLink, Navigate } from 'react-router';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { Button, Spinner } from './components/ui.jsx';
import Login from './pages/Login.jsx';
import Dashboard from './pages/Dashboard.jsx';
import CaseStudies from './pages/CaseStudies.jsx';
import Services from './pages/Services.jsx';
import Theme from './pages/Theme.jsx';
import Leads from './pages/Leads.jsx';
import Settings from './pages/Settings.jsx';
import Audit from './pages/Audit.jsx';
import Analytics from './pages/Analytics.jsx';
import Copy from './pages/Copy.jsx';
import Sections from './pages/Sections.jsx';
import VisualEditor from './pages/VisualEditor.jsx';

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/editor', label: 'Visual editor' },
  { to: '/content/work', label: 'Case studies' },
  { to: '/content/services', label: 'Services' },
  { to: '/copy', label: 'Copy' },
  { to: '/sections', label: 'Sections' },
  { to: '/leads', label: 'Leads' },
  { to: '/analytics', label: 'Analytics' },
  { to: '/theme', label: 'Theme' },
  { to: '/settings', label: 'Settings' },
  { to: '/audit', label: 'Audit log', role: 'ADMIN' },
];

function Sidebar() {
  const { user, logout, can } = useAuth();

  return (
    <aside className="flex w-60 shrink-0 flex-col justify-between border-r-3 border-ink bg-paper p-6">
      <div>
        <p className="font-display text-2xl">
          AGEN<span className="text-electric">Z</span>
        </p>
        <p className="mt-1 text-xs font-bold uppercase tracking-widest text-muted">Admin</p>

        <nav className="mt-10 space-y-1">
          {NAV.filter((item) => !item.role || can(item.role)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `block px-3 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${
                  isActive ? 'bg-ink text-paper' : 'hover:bg-acid'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="border-t-3 border-ink pt-5">
        <p className="text-sm font-semibold">{user?.name}</p>
        <p className="text-xs text-muted">@{user?.username}</p>
        <p className="text-xs text-muted">{user?.role}</p>
        <Button variant="ghost" size="sm" className="mt-3 w-full" onClick={logout}>
          Sign out
        </Button>
      </div>
    </aside>
  );
}

function Placeholder({ title, phase }) {
  return (
    <div className="border-brutal border-dashed bg-paper p-10">
      <h1 className="text-2xl">{title}</h1>
      <p className="mt-3 max-w-[60ch] text-muted">
        Ships in {phase}. The route exists now so the shell is complete and the build order
        stays visible.
      </p>
    </div>
  );
}

function AdminApp() {
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 overflow-x-auto p-10">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/editor" element={<VisualEditor />} />
          <Route path="/content/work" element={<CaseStudies />} />
          <Route path="/content/services" element={<Services />} />
          <Route path="/copy" element={<Copy />} />
          <Route path="/sections" element={<Sections />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/theme" element={<Theme />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/media" element={<Placeholder title="Media library" phase="Phase 3b" />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Gate() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Checking session…" />
      </div>
    );
  }

  return user ? <AdminApp /> : <Login />;
}

export default function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
